import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { jobNumber, jobName } from '../utils/ui';
import WorkflowPhotos from '../components/WorkflowPhotos';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

const SUB_JOB_COLORS = {
  'Vertical Drilling': '#e87722', 'Horizontal Drilling': '#2563eb', 'Directional Drilling': '#2563eb',
  'Loops': '#16a34a', 'Shop/Trucking': '#9333ea', 'Power Company': '#dc2626', 'General': '#6b7280',
};

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.json());
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(d, today))     return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function groupNotesByDate(notes) {
  const groups = {};
  notes.forEach(n => {
    const key = new Date(n.created_at).toDateString();
    if (!groups[key]) groups[key] = { label: formatDateLabel(n.created_at), notes: [] };
    groups[key].notes.push(n);
  });
  return Object.values(groups);
}

function NoteCard({ n }) {
  return (
    <div style={{ paddingTop: 12, borderTop: '1px solid var(--gray-border)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e87722', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
          {((n.first_name || '?')[0] + (n.last_name || '?')[0]).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{n.first_name} {n.last_name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-muted)' }}>
            {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
      </div>
      <p style={{ margin: '0 0 0 36px', fontSize: 14, lineHeight: 1.55, color: 'var(--text)' }}>{n.note}</p>
      {n.media?.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, marginLeft: 36, flexWrap: 'wrap' }}>
          {n.media.map((url, i) => /\.(mp4|mov|webm|avi)$/i.test(url)
            ? <video key={i} src={url} controls style={{ maxWidth: '100%', borderRadius: 8 }} />
            : <img key={i} src={url} alt="" onClick={() => window.open(url)} style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }} />
          )}
        </div>
      )}
    </div>
  );
}

function DateGroup({ label, notes, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ margin: '0 16px 8px', background: 'var(--gray-card)', borderRadius: 12, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', padding: '12px 14px', background: 'none', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{notes.length} update{notes.length !== 1 ? 's' : ''}</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-muted)' }}>
            <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          {notes.map(n => <NoteCard key={n.id} n={n} />)}
        </div>
      )}
    </div>
  );
}

function DailyUpdates({ notes }) {
  if (!notes.length) return null;
  const groups = groupNotesByDate(notes);
  return (
    <>
      <div className="section-head">DAILY UPDATES</div>
      {groups.map((g, i) => (
        <DateGroup key={g.label} label={g.label} notes={g.notes} defaultOpen={i === 0} />
      ))}
    </>
  );
}

function NoteModal({ projectId, projectName, onClose, onSubmitted }) {
  const [note, setNote]           = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listening, setListening]  = useState(false);
  const [result, setResult]        = useState(null);
  const [error, setError]          = useState(null);
  const recognitionRef = useRef(null);
  const fileInputRef   = useRef(null);

  function startVoice() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input not supported on this browser.'); return; }
    const r = new SR();
    r.lang = 'en-US'; r.continuous = true; r.interimResults = false;
    r.onresult = e => {
      const t = Array.from(e.results).map(r => r[0].transcript).join(' ');
      setNote(n => n ? n + ' ' + t : t);
    };
    r.onend = () => setListening(false);
    r.start();
    recognitionRef.current = r;
    setListening(true);
  }

  function stopVoice() { recognitionRef.current?.stop(); setListening(false); }

  async function submit() {
    if (!note.trim()) return;
    setSubmitting(true); setError(null);
    try {
      let mediaUrls = [];
      if (mediaFiles.length > 0) {
        setUploading(true);
        const token = getToken();
        const fd = new FormData();
        mediaFiles.forEach(f => fd.append('files', f));
        const up = await fetch(`${API_BASE}/upload`, {
          method: 'POST',
          headers: token ? { 'x-worker-token': token } : {},
          body: fd,
        });
        mediaUrls = (await up.json()).urls || [];
        setUploading(false);
      }
      const res = await apiFetch(`/projects/${projectId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note, mediaUrls }),
      });
      setResult(res);
      onSubmitted?.();
    } catch {
      setError('Could not submit. Try again.');
      setUploading(false);
    } finally { setSubmitting(false); }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ background: '#fff', borderRadius: '18px 18px 0 0', padding: '24px 20px 44px', boxShadow: '0 -4px 24px rgba(0,0,0,0.18)' }}>
        {result ? (
          <>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#111' }}>Update submitted ✓</div>
            {result.updatedSubJobs?.length > 0 && (
              <p style={{ color: '#666', fontSize: 14, lineHeight: 1.55, margin: '0 0 20px' }}>
                Logged to: <strong style={{ color: '#111' }}>{result.updatedSubJobs.join(', ')}</strong>
              </p>
            )}
            <button onClick={onClose} style={{ width: '100%', padding: '14px 0', background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>Daily Update</div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#999', lineHeight: 1, padding: 0 }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: '#999', marginBottom: 16 }}>{projectName}</div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.07em' }}>What did you work on today?</label>
              <button
                onMouseDown={e => { e.preventDefault(); listening ? stopVoice() : startVoice(); }}
                style={{ background: listening ? '#e87722' : '#f0f0f0', border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: listening ? '#fff' : '#666', cursor: 'pointer' }}
              >
                {listening ? '⏹ Stop' : '🎤 Speak'}
              </button>
            </div>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Describe what you worked on today…"
              rows={4}
              style={{ width: '100%', marginBottom: 12, padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${listening ? '#e87722' : '#ddd'}`, fontSize: 15, resize: 'none', background: '#fff', color: '#111', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5 }}
            />

            <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={e => setMediaFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f5f5f5', border: '1.5px dashed #ccc', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#666', cursor: 'pointer', width: '100%', justifyContent: 'center', marginBottom: 12, boxSizing: 'border-box' }}
            >📷 Add photos or video</button>

            {mediaFiles.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {mediaFiles.map((f, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    {f.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(f)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
                    ) : (
                      <div style={{ width: 72, height: 72, background: '#222', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>VIDEO</div>
                    )}
                    <button onClick={() => setMediaFiles(p => p.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#d00', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
              </div>
            )}

            {error && <div style={{ color: '#d00', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button
              onClick={submit}
              disabled={!note.trim() || submitting || uploading}
              style={{ width: '100%', padding: '14px 0', background: (!note.trim() || submitting) ? '#ccc' : '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >{uploading ? 'Uploading…' : submitting ? 'Submitting…' : 'Submit Update'}</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function CollapsibleTasks({ tasks }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: '0 16px 8px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', padding: '0 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <span className="section-head" style={{ padding: 0, margin: 0 }}>MY TASKS ({tasks.length})</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-muted)', flexShrink: 0 }}>
          <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && tasks.map(t => (
        <div key={t.id} style={{ background: 'var(--gray-card)', borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{t.title}</div>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: t.status === 'complete' ? '#16a34a' : t.status === 'active' ? '#2563eb' : '#6b7280', color: '#fff', fontWeight: 600 }}>
              {t.trello_list_name || t.status}
            </span>
          </div>
          {t.due_date && (
            <div style={{ fontSize: 12, color: 'var(--gray-muted)', marginTop: 4 }}>
              Due {new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Field crews log billable equipment time here. The rate lives on the office
// side (Equipment record) and is never sent to the PWA — crews only enter hours.
function EquipmentHoursCard({ projectId }) {
  const [subJobs, setSubJobs] = useState([]);
  const [drills, setDrills]   = useState([]);
  const [entries, setEntries] = useState({});
  const [forms, setForms]     = useState({});
  const [open, setOpen]       = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([apiFetch(`/projects/${projectId}/sub-jobs`).catch(() => []), apiFetch('/drill-options').catch(() => [])])
      .then(async ([sjs, dr]) => {
        if (!alive) return;
        const drilling = (Array.isArray(sjs) ? sjs : []).filter(s => s.type === 'vertical_drilling' || s.type === 'horizontal_drilling' || s.type === 'shop');
        setSubJobs(drilling); setDrills(Array.isArray(dr) ? dr : []);
        const results = await Promise.all(drilling.map(s => apiFetch(`/sub-jobs/${s.id}/drill-hours`).catch(() => [])));
        if (!alive) return;
        const map = {}; drilling.forEach((s, i) => { map[s.id] = Array.isArray(results[i]) ? results[i] : []; });
        setEntries(map);
      }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const draftFor = sjId => forms[sjId] || { equipment_id: '', work_date: new Date().toISOString().slice(0, 10), hours: '', note: '' };
  const setDraft = (sjId, patch) => setForms(f => ({ ...f, [sjId]: { ...draftFor(sjId), ...patch } }));

  async function log(sjId) {
    const d = draftFor(sjId);
    if (!d.equipment_id || !d.hours) return;
    const drill = drills.find(x => x.id === d.equipment_id);
    const saved = await apiFetch(`/sub-jobs/${sjId}/drill-hours`, { method: 'POST', body: JSON.stringify({ equipment_id: d.equipment_id, drill_name: drill ? drill.name : '', work_date: d.work_date, hours: d.hours, note: d.note }) });
    if (saved && saved.id) setEntries(e => ({ ...e, [sjId]: [...(e[sjId] || []), saved] }));
    setDraft(sjId, { equipment_id: '', hours: '', note: '' });
  }

  if (loading || subJobs.length === 0) return null;
  const fmtDate = d => { if (!d) return ''; const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d); return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, padding: '10px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };

  return (
    <div style={{ margin: '0 16px 12px' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', padding: '0 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <span className="section-head" style={{ padding: 0, margin: 0 }}>EQUIPMENT HOURS</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-muted)', flexShrink: 0 }}>
          <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && subJobs.map(sj => {
        const list = entries[sj.id] || [];
        const d = draftFor(sj.id);
        const total = list.reduce((s, r) => s + (Number(r.hours) || 0), 0);
        return (
          <div key={sj.id} style={{ background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--text)' }}>{sj.name || (sj.type === 'horizontal_drilling' ? 'Directional Drilling' : sj.type === 'shop' ? 'Shop / Equipment' : 'Vertical Drilling')}</div>
            {list.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {list.map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border, #f1f1f1)', color: 'var(--text)' }}>
                    <span>{fmtDate(r.work_date)} · {r.drill_name || 'Drill'}{r.note ? ` · ${r.note}` : ''}</span>
                    <strong>{r.hours} hrs</strong>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 8, fontWeight: 700, color: 'var(--text)' }}>
                  <span>Total</span><span>{total} hrs</span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <select value={d.equipment_id} onChange={e => setDraft(sj.id, { equipment_id: e.target.value })} style={inp}>
                <option value="">— Select Equipment —</option>
                {drills.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={d.work_date} onChange={e => setDraft(sj.id, { work_date: e.target.value })} style={{ ...inp, flex: 1 }} />
                <input type="number" inputMode="decimal" step="0.5" value={d.hours} onChange={e => setDraft(sj.id, { hours: e.target.value })} placeholder="Hours" style={{ ...inp, width: 100 }} />
              </div>
              <input value={d.note} onChange={e => setDraft(sj.id, { note: e.target.value })} placeholder="Note (optional)" style={inp} />
              <button onClick={() => log(sj.id)} disabled={!d.equipment_id || !d.hours}
                style={{ padding: '12px 0', background: (!d.equipment_id || !d.hours) ? '#c9b8ad' : '#e87722', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                + Log Hours
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function WorkerProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [project, setProject]   = useState(null);
  const [tasks, setTasks]       = useState([]);
  const [notes, setNotes]       = useState([]);
  const [pos, setPos]           = useState([]);
  const [mySubJobs, setMySubJobs] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showNote, setShowNote] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [showConversations, setShowConversations] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [convSearch, setConvSearch] = useState('');
  const [convLoading, setConvLoading] = useState(false);

  useEffect(() => { load(); loadConversations(); }, [id]);

  async function loadConversations() {
    if (convLoading || conversations.length > 0) return;
    setConvLoading(true);
    try {
      const data = await apiFetch(`/projects/${id}/conversations`);
      setConversations(Array.isArray(data) ? data : []);
    } catch {} finally { setConvLoading(false); }
  }

  function toggleConversations() {
    if (!showConversations) loadConversations();
    setShowConversations(s => !s);
  }

  async function load() {
    setLoading(true);
    try {
      const [proj, taskList, noteList, myProjects, poList] = await Promise.all([
        apiFetch(`/projects/${id}`),
        apiFetch(`/projects/${id}/tasks`),
        apiFetch(`/projects/${id}/notes`),
        apiFetch('/my-projects'),
        apiFetch(`/projects/${id}/purchase-orders`),
      ]);
      setProject(proj);
      setTasks(Array.isArray(taskList) ? taskList : []);
      setNotes(Array.isArray(noteList) ? noteList : []);
      setPos(Array.isArray(poList) ? poList : []);
      const mine = Array.isArray(myProjects) ? myProjects.find(p => p.id === id) : null;
      setMySubJobs(mine?.mySubJobs || []);
    } catch {} finally { setLoading(false); }
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="page">
      <div className="top-bar">
        <button
          onClick={() => navigate('/dashboard')}
          style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Projects</span>
        </button>
        <div style={{ color: '#7A7068', fontSize: 11 }}>{today}</div>
      </div>

      {loading ? (
        <div className="center-message"><div className="spinner" /><span>Loading…</span></div>
      ) : (
        <>
          {/* Project header */}
          <div style={{ padding: '20px 16px 12px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', lineHeight: 1.2 }}>
              {project ? <>{jobNumber(project) && <span style={{ color: '#e87722' }}>{jobNumber(project)} · </span>}{jobName(project)}</> : 'Project'}
            </div>
          </div>

          {/* Daily update button */}
          <div style={{ padding: '0 16px 16px' }}>
            <button
              onClick={() => setShowNote(true)}
              style={{ width: '100%', padding: '14px 0', background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >+ Submit Daily Update</button>
            <button
              onClick={() => navigate(`/project/${id}/workflows`)}
              style={{ width: '100%', marginTop: 10, padding: '13px 0', background: '#fff', color: '#1e3a5f', border: '2px solid #1e3a5f', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >🔀 Work Flows for this job</button>
            <button
              onClick={() => navigate(`/shop-request?project=${id}`)}
              style={{ width: '100%', marginTop: 10, padding: '13px 0', background: '#fff', color: '#475569', border: '2px solid #475569', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >📦 Request from Shop</button>
            <button
              onClick={() => navigate(`/jha?project=${id}`)}
              style={{ width: '100%', marginTop: 10, padding: '13px 0', background: '#fff', color: '#b45309', border: '2px solid #b45309', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >⚠️ Job Hazard Analysis</button>
          </div>

          {/* Equipment hours — shows on drilling & shop jobs */}
          <EquipmentHoursCard projectId={id} />

          {/* Field photos added from the drilling workflow card */}
          <div style={{ margin: '0 16px 12px' }}><WorkflowPhotos projectId={id} /></div>

          {/* Conversations — default open, collapsible */}
          <div style={{ margin: '0 16px 12px' }}>
            <button
              onClick={toggleConversations}
              style={{ width: '100%', background: 'none', border: 'none', padding: '0 0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            >
              <span className="section-head" style={{ padding: 0, margin: 0 }}>CONVERSATIONS</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ transform: showConversations ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'var(--gray-muted)', flexShrink: 0 }}>
                <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {showConversations && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  value={convSearch}
                  onChange={e => setConvSearch(e.target.value)}
                  placeholder="Search conversations…"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 13px', borderRadius: 10, border: '1.5px solid var(--border)', fontSize: 13, background: '#fff' }}
                />
                {convLoading && <div style={{ color: 'var(--gray-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>Loading…</div>}
                {!convLoading && conversations.length === 0 && (
                  <div style={{ color: 'var(--gray-muted)', fontSize: 13, textAlign: 'center', padding: 16 }}>No conversations yet. Submit a daily update to get started.</div>
                )}
                {conversations.map(conv => {
                  const q = convSearch.toLowerCase();
                  const filteredNotes = conv.notes.filter(n =>
                    !q || n.note?.toLowerCase().includes(q) || `${n.first_name} ${n.last_name}`.toLowerCase().includes(q)
                  );
                  if (q && filteredNotes.length === 0) return null;
                  return (
                    <div key={conv.name} style={{ background: 'var(--gray-card)', borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ padding: '10px 14px', background: '#f3f4f6', fontWeight: 700, fontSize: 13, color: '#374151' }}>
                        {conv.name}
                      </div>
                      {conv.summary && (
                        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', fontSize: 13, color: '#374151', lineHeight: 1.55, fontStyle: 'italic', background: '#fafafa' }}>
                          {conv.summary}
                        </div>
                      )}
                      {(filteredNotes.length === 0 ? conv.notes : filteredNotes).slice(0, 10).map(note => (
                        <div key={note.id} style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{note.first_name} {note.last_name}</span>
                            <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>
                              {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{note.note}</p>
                        </div>
                      ))}
                      {conv.notes.length === 0 && (
                        <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--gray-muted)' }}>No notes yet.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Daily updates — date accordion */}
          <DailyUpdates notes={notes} />

          {/* Tasks — collapsed by default */}
          {tasks.length > 0 && <CollapsibleTasks tasks={tasks} />}

          {/* Purchase Orders */}
          {pos.length > 0 && (
            <>
              <div className="section-head" style={{ marginTop: 4 }}>PURCHASE ORDERS ({pos.length})</div>
              {pos.map(po => (
                <div
                  key={po.id}
                  onClick={() => worker?.canCreatePo ? setEditingPO(po) : null}
                  style={{ margin: '0 16px 8px', background: 'var(--gray-card)', borderRadius: 12, padding: '12px 14px', cursor: worker?.canCreatePo ? 'pointer' : 'default' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 17, fontWeight: 900, color: '#e87722' }}>#{po.po_number}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700, background: { pending: '#f59e0b22', approved: '#16a34a22', completed: '#6b728022', rejected: '#dc262622' }[po.status] || '#f59e0b22', color: { pending: '#b45309', approved: '#15803d', completed: '#374151', rejected: '#b91c1c' }[po.status] || '#b45309' }}>
                      {po.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text)', marginTop: 4, lineHeight: 1.4 }}>{po.description}</div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                    {po.vendor && <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>📦 {po.vendor}</span>}
                    {po.cost != null && <span style={{ fontSize: 12, color: 'var(--gray-muted)' }}>💰 ${parseFloat(po.cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </div>
                  {worker?.canCreatePo && <div style={{ fontSize: 11, color: '#e87722', marginTop: 6, fontWeight: 600 }}>Tap to edit →</div>}
                </div>
              ))}
            </>
          )}

          <div style={{ height: 40 }} />
        </>
      )}

      {showNote && (
        <NoteModal
          projectId={id}
          projectName={project?.name}
          onClose={() => setShowNote(false)}
          onSubmitted={() => { setShowNote(false); load(); }}
        />
      )}

      {editingPO && (
        <POEditModal
          po={editingPO}
          onClose={() => setEditingPO(null)}
          onSaved={updated => {
            setPos(ps => ps.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            setEditingPO(null);
          }}
        />
      )}
    </div>
  );
}

function POEditModal({ po, onClose, onSaved }) {
  const [description, setDescription] = useState(po.description || '');
  const [vendor, setVendor] = useState(po.vendor || '');
  const [cost, setCost] = useState(po.cost != null ? String(po.cost) : '');
  const [notes, setNotes] = useState(po.notes || '');
  const [status, setStatus] = useState(po.status || 'pending');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    if (!description.trim()) { setError('Description is required.'); return; }
    setSaving(true);
    try {
      await apiFetch(`/purchase-orders/${po.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ description: description.trim(), vendor: vendor.trim() || null, cost: cost !== '' ? parseFloat(cost) : null, notes: notes.trim() || null, status }),
      });
      onSaved({ id: po.id, description: description.trim(), vendor: vendor.trim() || null, cost: cost !== '' ? parseFloat(cost) : null, notes: notes.trim() || null, status });
    } catch { setError('Failed to save. Please try again.'); }
    setSaving(false);
  }

  const overlayStyle = { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' };
  const sheetStyle = { background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px', paddingBottom: 'calc(24px + env(safe-area-inset-bottom))', width: '100%', maxHeight: '85vh', overflowY: 'auto' };

  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Purchase Order</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#e87722' }}>#{po.po_number}</div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Description *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Vendor</label>
              <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="Optional" style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Cost ($)</label>
              <input type="number" min="0" step="0.01" value={cost} onChange={e => setCost(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, background: '#fff' }}>
              {['pending','approved','completed','rejected'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional" style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
          <button onClick={save} disabled={saving} style={{ padding: '14px', borderRadius: 12, border: 'none', background: saving ? '#ccc' : '#e87722', color: '#fff', fontSize: 16, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
